import ApiService from '../../../services/apiService';
import { mapState } from 'vuex';

// Map roleId to `DigitalTwin.DigitalTwin` i18n key
// Handled like this so roleIds can be read from DB later
const roleIdToNameMap = {
  0: '0',
  1: '1'
};
export default {
  name: 'CoreDtwinUserRelations',
  props: {
    scopeId: {
      type: Number,
      required: true
    },
    twinDataRelations: {
      type: Array,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
    isOpen: {
      type: Boolean,
      default: true
    }
  },
  data: function () {
    return {
      addUserRelationDialog: false,
      inputUserId: null,
      inputRoleId: null,
      search: null,
      formIsValid: false,
      storeEditRelation: {},
      twinDataUsers: [],
      selectRoleItems: [],
      userRelations: [],
      headers: [{
        text: this.$i18n_t('DigitalTwin.DigitalTwin.userRelationsTableName'),
        value: 'name',
        align: 'start'
      }, {
        text: this.$i18n_t('DigitalTwin.DigitalTwin.userRelationsTableRole'),
        value: 'role',
        align: 'start'
      }, {}, {}],
      tableFooterProps: {
        itemsPerPageText: this.$i18n_t('Common.rowsPerPage'),
        itemsPerPageOptions: [20, 50, {
          text: this.$i18n_t('Common.all'),
          value: -1
        }]
      }
    };
  },
  computed: {
    ...mapState('app', ['snackbar', 'inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme']),
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    buttons() {
      return [{
        title: this.$i18n_t('DigitalTwin.DigitalTwin.attachmentButtonAdd'),
        icon: 'fal fa-plus',
        action: 'addItem',
        valid: !this.disabled
      }];
    }
  },
  mounted() {
    this.resetForm();
    this.init();
  },
  methods: {
    init() {
      // only fetch the first 200 users; to be figured out what to do for the other ones
      ApiService.fetchUsers([], ['ancestors'], [], 0, 200).then(users => {
        this.twinDataUsers = users.users.map(user => ({
          id: user.id,
          name: `${user.firstName} ${user.lastName}`
        }));
        this.userRelations = this.mapNamesToRelations(this.twinDataRelations);
        this.selectRoleItems = this.createSelectRoleItems();
      }).catch(error => {
        console.error(error);
      });
    },
    addItem() {
      this.$refs.attachmentInput.click();
    },
    mapNamesToRelations(relationData) {
      return relationData.map(item => {
        const userObject = this.findUserById(item.userId);
        return {
          userId: userObject.id,
          roleId: item.roleId,
          name: userObject.name,
          role: this.findRoleNameById(item.roleId)
        };
      })
      // Filter out users without userId, that would be those that were
      // listed in the user/twin relations but do not exist in allowed users
      // for the twin. (Users that are deleted or lost access in the meantime)
      .filter(item => !!item.userId);
    },
    createSelectRoleItems() {
      return Object.keys(roleIdToNameMap).map(roleId => ({
        roleId: parseInt(roleId, 10),
        role: this.$i18n_t(`DigitalTwin.DigitalTwin.userRelationsRoles.${roleIdToNameMap[roleId]}`)
      }));
    },
    findUserById(userId) {
      const userObject = this.twinDataUsers.slice(0).filter(user => user.id === userId);
      return userObject.length > 0 ? userObject[0] : {};
    },
    findRoleNameById(roleId) {
      const i18nRole = this.$i18n_t(`DigitalTwin.DigitalTwin.userRelationsRoles.${roleIdToNameMap[roleId]}`);
      if (i18nRole.startsWith('DigitalTwin.DigitalTwin.userRelationsRoles')) {
        // If no i18n string for the roleId could be found, fall back to error message
        return this.$i18n_t('DigitalTwin.DigitalTwin.userRelationsRolesUnknown');
      }
      return i18nRole;
    },
    resetForm() {
      this.search = null;
      this.inputUserId = null;
      this.inputRoleId = null;
      this.formIsValid = false;
      this.storeEditRelation = {};
    },
    openAddUserRelationDialog() {
      this.resetForm();
      this.addUserRelationDialog = true;
    },
    closeAddUserRelationDialog() {
      this.addUserRelationDialog = false;
    },
    validateForm() {
      if (!this.inputUserId) {
        this.search = null;
      }
      this.formIsValid = Boolean(this.inputUserId) && !isNaN(parseInt(this.inputRoleId, 10));
    },
    updateUserRelation(userId, roleId) {
      this.openAddUserRelationDialog();
      this.$nextTick(() => {
        this.storeEditRelation = {
          userId: userId,
          roleId: roleId
        };
        this.inputUserId = userId;
        this.inputRoleId = roleId;
        this.validateForm();
      });
    },
    deleteUserRelation(userId, roleId) {
      const newUserRelations = this.userRelations.filter(user => !(user.userId === userId && user.roleId === roleId));
      const storeUserRelations = newUserRelations.map(user => ({
        userId: user.userId,
        roleId: user.roleId
      }));
      this.userRelations = this.mapNamesToRelations(storeUserRelations);
      this.$emit('update', storeUserRelations);
    },
    isEntryDuplicated(userId, roleId) {
      // Compare selected userid-roleId to existing entries in userRelations
      // to find if a combination already exists
      const foundDuplicates = this.userRelations.filter(item => item.userId === userId && item.roleId === roleId);
      return foundDuplicates.length > 0;
    },
    handleSubmit() {
      this.closeAddUserRelationDialog();

      // Check if entry already exists and if so show an error message
      if (this.isEntryDuplicated(this.inputUserId, this.inputRoleId)) {
        this.$snack(this.$i18n_t('DigitalTwin.DigitalTwin.userRelationErrorDuplicate'), 'error');
        return;
      }

      // Strip unwanted info from relations data
      let newRelations = this.userRelations.map(item => ({
        userId: item.userId,
        roleId: item.roleId
      }));
      if (this.storeEditRelation.userId) {
        // replace onld entry with new one
        newRelations = newRelations.map(item => {
          const shouldUpdate = item.userId === this.storeEditRelation.userId && item.roleId === this.storeEditRelation.roleId;
          return {
            userId: shouldUpdate ? this.inputUserId : item.userId,
            roleId: shouldUpdate ? this.inputRoleId : item.roleId
          };
        });
      } else {
        // add new entry
        newRelations.push({
          userId: this.inputUserId,
          roleId: this.inputRoleId
        });
      }
      this.userRelations = this.mapNamesToRelations(newRelations);
      this.$emit('update', newRelations);
    }
  }
};